// Port of webapp/src/cardFilter.ts (the clause/group evaluation core).
// Condition semantics — including the "empty values means ignore the clause"
// rule and the half-day windowing for createdTime/updatedTime — follow the
// original implementation.

import {IPropertyTemplate} from '../types/board'
import {Card} from '../types/card'
import {FilterClause, FilterGroup, isAFilterGroupInstance} from '../types/filterGroup'
import {createDatePropertyFromString, DateProperty} from './properties'

const halfDay = 12 * 60 * 60 * 1000

class CardFilter {
    static applyFilterGroup(filterGroup: FilterGroup, templates: readonly IPropertyTemplate[], cards: Card[]): Card[] {
        return cards.filter((card) => CardFilter.isFilterGroupMet(filterGroup, templates, card))
    }

    static isFilterGroupMet(filterGroup: FilterGroup, templates: readonly IPropertyTemplate[], card: Card): boolean {
        const {operation, filters} = filterGroup

        if (operation === 'and') {
            for (const filter of filters) {
                if (isAFilterGroupInstance(filter) && !CardFilter.isFilterGroupMet(filter, templates, card)) {
                    return false
                }
                if (!isAFilterGroupInstance(filter) && !CardFilter.isClauseMet(filter, templates, card)) {
                    return false
                }
            }
            return true
        }

        if (operation === 'or') {
            if (filters.length === 0) {
                return true
            }
            for (const filter of filters) {
                if (isAFilterGroupInstance(filter)) {
                    if (CardFilter.isFilterGroupMet(filter, templates, card)) {
                        return true
                    }
                } else if (CardFilter.isClauseMet(filter, templates, card)) {
                    return true
                }
            }
            return false
        }

        return true
    }

    static isClauseMet(filter: FilterClause, templates: readonly IPropertyTemplate[], card: Card): boolean {
        let value: string | string[] | number | undefined = card.fields.properties[filter.propertyId]
        if (filter.propertyId === 'title') {
            value = card.title.toLowerCase()
        }

        const template = templates.find((o) => o.id === filter.propertyId)
        let dateValue: DateProperty | undefined
        if (template?.type === 'date') {
            dateValue = createDatePropertyFromString(value as string)
        }

        if (!value && template) {
            if (template.type === 'createdBy') {
                value = card.createdBy
            } else if (template.type === 'updatedBy') {
                value = card.modifiedBy
            } else if (template.type === 'createdTime') {
                value = card.createAt.toString()
                dateValue = createDatePropertyFromString(value)
            } else if (template.type === 'updatedTime') {
                value = card.updateAt.toString()
                dateValue = createDatePropertyFromString(value)
            }
        }

        const textValue = () => (Array.isArray(value) ? value.join(',') : String(value ?? '')).toLowerCase()

        switch (filter.condition) {
        case 'includes':
            if (filter.values.length < 1) {
                break
            }
            return filter.values.some((cValue) => (Array.isArray(value) ? value.includes(cValue) : cValue === value))
        case 'notIncludes':
            if (filter.values.length < 1) {
                break
            }
            return !filter.values.some((cValue) => (Array.isArray(value) ? value.includes(cValue) : cValue === value))
        case 'isEmpty':
            return textValue().length <= 0
        case 'isNotEmpty':
            return textValue().length > 0
        case 'isSet':
            return Boolean(value)
        case 'isNotSet':
            return !value
        case 'is': {
            if (filter.values.length === 0) {
                return true
            }
            if (dateValue !== undefined) {
                const numericFilter = parseInt(filter.values[0], 10)
                if (template && (template.type === 'createdTime' || template.type === 'updatedTime')) {
                    // created/updated time include the time, so compare a ±12h window
                    return Boolean(dateValue.from) && dateValue.from! > (numericFilter - halfDay) && dateValue.from! < (numericFilter + halfDay)
                }
                if (dateValue.from && dateValue.to) {
                    return dateValue.from <= numericFilter && dateValue.to >= numericFilter
                }
                return dateValue.from === numericFilter
            }
            return filter.values[0]?.toLowerCase() === value
        }
        case 'contains':
            if (filter.values.length === 0) {
                return true
            }
            return textValue().includes(filter.values[0]?.toLowerCase())
        case 'notContains':
            if (filter.values.length === 0) {
                return true
            }
            return !textValue().includes(filter.values[0]?.toLowerCase())
        case 'startsWith':
            if (filter.values.length === 0) {
                return true
            }
            return textValue().startsWith(filter.values[0]?.toLowerCase())
        case 'notStartsWith':
            if (filter.values.length === 0) {
                return true
            }
            return !textValue().startsWith(filter.values[0]?.toLowerCase())
        case 'endsWith':
            if (filter.values.length === 0) {
                return true
            }
            return textValue().endsWith(filter.values[0]?.toLowerCase())
        case 'notEndsWith':
            if (filter.values.length === 0) {
                return true
            }
            return !textValue().endsWith(filter.values[0]?.toLowerCase())
        case 'isBefore': {
            if (filter.values.length === 0) {
                return true
            }
            if (dateValue !== undefined) {
                const numericFilter = parseInt(filter.values[0], 10)
                if (template && (template.type === 'createdTime' || template.type === 'updatedTime')) {
                    return Boolean(dateValue.from) && dateValue.from! < (numericFilter - halfDay)
                }
                return dateValue.from ? dateValue.from < numericFilter : false
            }
            return false
        }
        case 'isAfter': {
            if (filter.values.length === 0) {
                return true
            }
            if (dateValue !== undefined) {
                const numericFilter = parseInt(filter.values[0], 10)
                if (template && (template.type === 'createdTime' || template.type === 'updatedTime')) {
                    return Boolean(dateValue.from) && dateValue.from! > (numericFilter + halfDay)
                }
                if (dateValue.to) {
                    return dateValue.to > numericFilter
                }
                return dateValue.from ? dateValue.from > numericFilter : false
            }
            return false
        }
        default:
            console.warn(`[cardFilter] unknown condition: ${String(filter.condition)}`)
        }

        // "No values = ignore clause (always met)" for the option-based conditions
        return true
    }

    /** Which properties a card must already set for this filter to show it. */
    static propertiesThatMeetFilterGroup(filterGroup: FilterGroup | undefined, templates: readonly IPropertyTemplate[]): Record<string, string> {
        if (!filterGroup) {
            return {}
        }
        const result: Record<string, string> = {}
        filterGroup.filters.forEach((filter) => {
            if (isAFilterGroupInstance(filter)) {
                return
            }
            const template = templates.find((o) => o.id === filter.propertyId)
            if (template && filter.values.length === 1 && ['includes', 'is'].includes(filter.condition)) {
                result[filter.propertyId] = filter.values[0]
            }
        })
        return result
    }
}

export {CardFilter}
